import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { CloudSettingsState } from './cloudSettingsState'

const safeHead = <T>(list: T[] | undefined | null): T | undefined => {
  if (!Array.isArray(list) || list.length === 0) return undefined
  return list[0]
}

const EMPTY_ACCOUNTS: Array<{
  id?: string
  product?: string
  cloud?: string
}> = []

const parseProductPathSegments = (pathname: string, product: string) => {
  const match = pathname.match(new RegExp(`/${product}/([^/]+)/([^/]+)`))
  if (!match) return null
  return {
    cloud: match[1],
    accountId: match[2],
  }
}

export const useCloudSettingsAccountId = ({
  product,
  cloud,
}: {
  product: string
  cloud?: string
}) => {
  const { accountId: accountIdFromParams } = useParams<{ accountId: string }>()
  const rawAccountIdFromPath = parseProductPathSegments(
    location.pathname,
    product
  )?.accountId
  const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  const accountIdFromPath =
    rawAccountIdFromPath && UUID_REGEX.test(rawAccountIdFromPath)
      ? rawAccountIdFromPath
      : undefined

  const selectedAccountIds = useSelector(
    (state: CloudSettingsState) => state.accountSelection?.selectedAccountIds
  )
  const availableAccounts = useSelector((state: CloudSettingsState) => {
    const accounts = state.accountSelection?.availableAccounts
    return Array.isArray(accounts) ? accounts : EMPTY_ACCOUNTS
  })
  const accountData = useMemo(
    () =>
      availableAccounts.filter(
        account => account?.product?.toLowerCase() === product
      ),
    [availableAccounts, product]
  )

  if (accountIdFromParams) {
    return accountIdFromParams
  }

  if (accountIdFromPath) {
    return accountIdFromPath
  }

  const accountIdForCloud = (requestedCloud: string) =>
    accountData.find(account => account?.cloud === requestedCloud)?.id || ''

  if (product.toLowerCase() === 'flyout') {
    const flyoutSelections =
      selectedAccountIds?.['FLYOUT'] || selectedAccountIds?.['flyout']

    if (cloud) {
      return flyoutSelections?.[cloud] || accountIdForCloud(cloud)
    }

    const selectedCloud = Object.keys(flyoutSelections || {})[0]
    if (selectedCloud) {
      return flyoutSelections?.[selectedCloud] || ''
    }
  }

  if (cloud) {
    return accountIdForCloud(cloud)
  }
  return safeHead(accountData)?.id || ''
}
